import { useState } from 'react'

import { AccountSwitcher } from '@/components/AccountSwitcher'
import type { Health } from '@/lib/api'
import type { AccountsState, ExecutionMode } from '@/lib/types'

const MODE_LABELS: Record<ExecutionMode, string> = {
  calculator: 'CALCULATEUR',
  confirmation: 'CONFIRMATION',
  auto: 'AUTO',
}

/**
 * Bandeau d'état permanent, en haut de l'écran.
 *
 * Il répond en un coup d'œil à la seule question qui compte avant de cliquer :
 * « sur quel compte part mon ordre, et est-ce de l'argent réel ? ». Depuis les
 * comptes multiples, la réponse dépend du compte ACTIF et non plus du serveur :
 * un même utilisateur peut avoir un compte de démonstration et un compte réel.
 * Le compte réel est signalé en rouge plein, impossible à confondre.
 */
export function StatusBar({
  health,
  error,
  mode,
  accounts,
  onAccountsChange,
  compact,
  onReconnect,
}: {
  health: Health | null
  error: string | null
  mode: ExecutionMode
  accounts: AccountsState | null
  onAccountsChange: (state: AccountsState) => void
  /** Résumé posé à la place du nom de l'application sur téléphone, où ce nom
      n'apprend rien à qui a déjà ouvert l'application. */
  compact?: React.ReactNode
  /** Rouvre la liaison du compte actif et recharge l'écran. */
  onReconnect?: () => Promise<void>
}) {
  const [reconnecting, setReconnecting] = useState(false)
  const active = accounts?.accounts.find((account) => account.id === accounts.active_id)
  // Le compte actif fait foi. À défaut — liste pas encore chargée —, la
  // configuration du serveur.
  const live = active ? active.is_live : (health?.live_trading ?? false)

  return (
    // `shrink-0` et non plus `sticky` : la coque est une colonne flex de
    // hauteur fixe, seul le contenu défile. Un `sticky` dans un conteneur qui
    // ne défile pas ne sert à rien et laissait un espace fantôme.
    <header className="safe-top safe-x z-20 shrink-0 border-b border-border bg-panel/95">
      <div className="flex items-center justify-between gap-2 px-3 py-2 sm:px-4">
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          {/* Sur téléphone, la place du nom revient à ce qui change : les
              positions ouvertes et leur résultat. Le nom reste sur
              ordinateur, où la largeur ne manque pas. */}
          {compact && <div className="min-w-0 flex-1 md:hidden">{compact}</div>}
          <span
            className={`text-[15px] font-semibold tracking-tight text-ink-strong ${
              compact ? 'hidden md:inline' : ''
            }`}
          >
            Reverse<span className="text-accent">Trader</span>
          </span>
          {/* Le mode se lit aussi dans les réglages : sur téléphone, la place
              revient au compte, qui compte davantage. */}
          <span className="num hidden rounded border border-border bg-elevated px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-ink-muted sm:inline">
            {MODE_LABELS[mode]}
          </span>
        </div>

        <div className="flex min-w-0 items-center gap-2">
          {error ? (
            <Badge tone="down">BACKEND HORS LIGNE</Badge>
          ) : accounts ? (
            <>
              <AccountSwitcher state={accounts} onChange={onAccountsChange} />
              {/*
                Le recours quand l'écran reste muet — prix figés, bouton
                d'ordre absent — sans que rien ne dise pourquoi. Il rouvre la
                liaison avec le courtier ET recharge l'écran : l'un ou l'autre
                peut être en cause, et il n'appartient pas à l'utilisateur de
                deviner lequel. La coupure automatique, elle, n'est jamais
                réarmée par ce bouton.
              */}
              {onReconnect && (
                <button
                  type="button"
                  disabled={reconnecting || Boolean(active?.starting)}
                  onClick={async () => {
                    setReconnecting(true)
                    try {
                      await onReconnect()
                    } finally {
                      setReconnecting(false)
                    }
                  }}
                  aria-label="Reconnecter le courtier et rafraîchir"
                  title="Reconnecter le courtier et rafraîchir"
                  className="flex min-h-touch w-9 shrink-0 items-center justify-center rounded-md border border-border bg-elevated text-[16px] text-ink-muted hover:bg-border/60 active:bg-border disabled:opacity-60"
                >
                  <span
                    aria-hidden
                    className={reconnecting || active?.starting ? 'inline-block animate-spin' : ''}
                  >
                    ↻
                  </span>
                </button>
              )}
            </>
          ) : health ? (
            <Badge tone={health.broker_connected ? 'up' : 'muted'}>
              {health.broker_connected ? 'BROKER CONNECTÉ' : 'BROKER DÉCONNECTÉ'}
            </Badge>
          ) : (
            <Badge tone="muted">CHARGEMENT…</Badge>
          )}
        </div>
      </div>

      {live && (
        <div className="bg-down px-4 py-1 text-center text-[12px] font-semibold text-white">
          Compte réel actif — les ordres engagent de l'argent réel
        </div>
      )}
    </header>
  )
}

function Badge({ tone, children }: { tone: 'up' | 'down' | 'muted'; children: React.ReactNode }) {
  const tones = {
    up: 'border-up/50 bg-up/15 text-up',
    down: 'border-down/50 bg-down/15 text-down',
    muted: 'border-border bg-elevated text-ink-muted',
  } as const

  return (
    <span
      className={`num rounded border px-2 py-0.5 text-[10px] font-semibold tracking-wide ${tones[tone]}`}
    >
      {children}
    </span>
  )
}
